import { z } from "zod";
import { IsoDateTime, OpaqueId, Position } from "./common.js";
import { PositionType } from "./journey.js";
import { Deviation } from "./deviation.js";
import { JunctionAlert } from "./junction.js";
/**
 * Ponctualité (§12.3), rejeu de journée (§12.4), passation de service (§12.8),
 * journal d'audit régulateur (§12.9). Tout est en lecture.
 */
// --- Ponctualité -------------------------------------------------------
export const PunctualityDaysResponse = z.object({
    days: z.array(z.string()), // "YYYY-MM-DD", du plus récent au plus ancien
});
/**
 * D-023 — décompte en 3 bandes, aligné sur le tableau de bord exploitant.
 *  - `onTime`   : conforme (écart dans la tolérance `[early, late]`)
 *  - `early`    : avance retenue (bande `(avanceFloor, early]`, ~1 à 3 min d'avance)
 *  - `late`     : retard retenu (bande `[late, retardCeiling)`, ~5 à 10 min de retard)
 *  - `excluded` : hors bande (avance ≥ 3 min ou retard ≥ 10 min) — exclu des taux
 * `onTime + early + late = sampleCount` (le dénominateur des taux).
 */
export const PunctualityCounts = z.object({
    onTime: z.number().int().nonnegative(),
    early: z.number().int().nonnegative(),
    late: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
});
/** Bloc de taux + décomptes commun au réseau, aux groupes et aux lignes (D-023). */
const punctualityRates = {
    onTimeRatio: z.number().min(0).max(1), // conforme / retenu
    earlyRatio: z.number().min(0).max(1), // avance / retenu
    lateRatio: z.number().min(0).max(1), // retard / retenu — les 3 taux somment à 1 (± arrondi)
    counts: PunctualityCounts,
    sampleCount: z.number().int().nonnegative(), // = onTime + early + late (dénominateur des taux)
    measuredCount: z.number().int().nonnegative(), // brut, avant exclusion (sampleCount + counts.excluded)
};
export const LinePunctuality = z.object({
    lineId: OpaqueId,
    lineCode: z.string(),
    ...punctualityRates,
    averageDeviationSeconds: z.number().int(), // moyenne sur le dénominateur retenu
});
export const GroupPunctuality = z.object({
    groupId: z.string(), // "tr-metro" | "tr-teor" | "tr-fast" | "tr-bus" pour Astuce
    label: z.string(), // "Métro" | "TEOR" | "FAST" | "Bus"
    ...punctualityRates,
});
export const PunctualityReport = z.object({
    date: z.string(), // YYYY-MM-DD (jour d'exploitation)
    provisional: z.boolean(), // true = jour non clôturé, calculé à la volée, non stocké
    period: z.string().nullable(), // régime d'exploitation du jour (§12.6) ; null = réseau fermé
    network: z.object(punctualityRates),
    groups: z.array(GroupPunctuality), // par mode (Métro / TEOR / FAST / Bus)
    lines: z.array(LinePunctuality), // triées par onTimeRatio décroissant (classement)
    computedAt: IsoDateTime.nullable(), // horodatage du figeage ; null si provisoire
});
/** Agrégat mensuel de ponctualité (P2-35) — conservé indéfiniment (D4). */
export const PunctualityMonth = z.object({
    month: z.string(), // YYYY-MM
    ...punctualityRates,
    dayCount: z.number().int().nonnegative(), // jours figés roulés dans l'agrégat
    lines: z.array(LinePunctuality),
});
export const PunctualityMonthlyResponse = z.object({
    months: z.array(PunctualityMonth), // du plus récent au plus ancien
});
// --- Rejeu de journée ------------------------------------------------
export const RecordingDaysResponse = z.object({
    days: z.array(z.string()),
});
/** Une trame NDJSON de `GET /recordings/{date}` (Content-Type application/x-ndjson). */
export const RecordingFrame = z.object({
    t: IsoDateTime,
    vehicles: z.array(z.object({
        journeyId: OpaqueId,
        lineId: OpaqueId,
        vehicleDisplayNumber: z.string(),
        position: Position.pick({ lat: true, lon: true, bearing: true }),
        positionType: PositionType,
        deviationSeconds: z.number().int(),
    })),
});
// --- Passation de service ------------------------------------------
export const RegulatorAuditEntry = z.object({
    post: z.string(),
    action: z.string(),
    target: z.string(),
    at: IsoDateTime,
});
export const RegulatorAuditResponse = z.object({
    entries: z.array(RegulatorAuditEntry),
});
export const HandoverResponse = z.object({
    at: IsoDateTime,
    post: z.string().nullable(),
    deviations: z.array(Deviation), // toutes les entrées actives, acquittées ou non
    flaggedJunctions: z.array(JunctionAlert),
    recentRegulatorActions: z.array(RegulatorAuditEntry),
});
//# sourceMappingURL=observability.js.map